import React from 'react';
import { Search, X, ArrowRight } from 'lucide-react';
import { ArchitectureNode } from '../types';
import { ComponentType } from '../types';

interface SearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: ArchitectureNode[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onJumpToNode: (nodeId: string) => void;
}

const SearchPanel: React.FC<SearchPanelProps> = ({
  isOpen,
  onClose,
  nodes,
  searchTerm,
  onSearchChange,
  onJumpToNode
}) => {
  if (!isOpen) return null;

  const filteredNodes = nodes.filter(node => {
    if (!searchTerm) return false;
    const term = searchTerm.toLowerCase();
    const label = node.data.label.toLowerCase();
    const type = node.data.type.toLowerCase();
    const cpu = node.data.specs?.cpu?.toLowerCase() || '';
    const memory = node.data.specs?.memory?.toLowerCase() || '';
    const engine = node.data.specs?.engine?.toLowerCase() || '';
    
    return label.includes(term) || 
           type.includes(term) || 
           cpu.includes(term) || 
           memory.includes(term) || 
           engine.includes(term);
  });

  return (
    <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-white border border-black/10 rounded-lg shadow-xl z-[100] w-[400px] max-w-[90vw] animate-slide-in-down will-change-transform">
      <div className="p-4 border-b border-black/10 flex items-center gap-2">
        <Search size={18} className="text-gray-400" />
        <input
          type="text"
          placeholder="Search components..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="flex-1 outline-none text-sm text-black placeholder-gray-400 transition-all duration-200 focus:scale-[1.02]"
          autoFocus
        />
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-black transition-colors"
        >
          <X size={18} />
        </button>
      </div>
      
      <div className="max-h-[300px] overflow-y-auto">
        {searchTerm && filteredNodes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-sm">No components found</p>
          </div>
        ) : filteredNodes.length > 0 ? (
          <div className="p-2">
            <div className="text-xs text-gray-500 px-2 py-1 mb-1">
              {filteredNodes.length} component{filteredNodes.length !== 1 ? 's' : ''} found
            </div>
            {filteredNodes.map((node, index) => (
              <button
                key={node.id}
                onClick={() => {
                  onJumpToNode(node.id);
                  onClose();
                }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-md transition-all duration-200 flex items-center justify-between group animate-fade-in hover:scale-[1.01] active:scale-95"
                style={{ 
                  animationDelay: `${index * 30}ms`,
                  animationDuration: '200ms',
                  animationFillMode: 'both'
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-black truncate">
                    {node.data.label}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {node.data.type}
                    {node.data.specs?.cpu && ` • ${node.data.specs.cpu}`}
                    {node.data.specs?.memory && ` • ${node.data.specs.memory}`}
                  </div>
                </div>
                <ArrowRight size={16} className="text-gray-400 group-hover:text-black ml-2 flex-shrink-0" />
              </button>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-400">
            <Search size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Start typing to search components</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPanel;
